import React, { useState, useEffect, useMemo, useCallback, useContext } from "react";
import { useParams, useHistory } from "react-router-dom";
import { secureGet, securePost } from "../../config/api";
import { formatDateTime, formatAmount } from "../../utils/format";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { secureGetPdf } from "../../config/api";
import DeletePopup from "../../components/DeletePopup";
import GeneratePopup from "../../components/GeneratePopUp";
import ProductRow from "../../components/ProductRow";
import LoadingLogo from "../../components/LoadingLogo";
import SnackbarNotification from "../../components/SnackbarNotification";
import Breadcrumbs from "../../components/Breadcrumbs";
import UserContext from "../../Shared/UserContext";

import downloadIcon from "../../icons/printer.svg";
import checkIcon from "../../icons/check.svg";

const OrderDetails = () => {
  const { id } = useParams();
  const history = useHistory();
  const { t } = useTranslation();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const { state: { role } } = useContext(UserContext);

  const showSnackbar = (message, severity = 'success', manual = false) => {
    setSnackbar({ open: true, message, severity, manual });
  };

  const abortController = useMemo(() => new AbortController(), []);
  const { signal } = abortController;

  const fetchOrder = useCallback(async () => {
    setLoading(true);
    try {
      const response = await secureGet(`/carts/${id}`, showSnackbar, { signal });
      const fetchedOrder = response.data;
      setOrder(fetchedOrder);
      console.log("fetchedOrder", fetchedOrder);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error("Failed to fetch order:", error);
      }
    } finally {
      setLoading(false);
    }
  }, [id, signal]);

  useEffect(() => {
    fetchOrder();

    return () => {
      abortController.abort();
    };
  }, [id, abortController, fetchOrder]);


  const handleUpdateStatus = async (status) => {
    setUpdatingStatus(true);
    try {
      const payload = {
        cartId: order.id,
        status: status,
      };
      await securePost(`/carts/updateStatus`, payload, showSnackbar);
      showSnackbar(t(`confirmation.orderStatusUpdate`), 'success');
      fetchOrder();
    } catch (error) {
      console.error("Error updating the order status:", error);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const renderStatut = () => (

    <div className="flex items-center ">
      <span
        className="px-[5px] py-1 rounded-[10px] font-semibold text-[12px] bg-[#0AD69F] text-white"
      >
        BC
      </span>
      <div className="w-2 h-[2px] bg-[#043F88]"></div>

      <span
        className={`px-[5px] py-1 rounded-[10px] font-semibold text-[12px] ${order.deliveryNote ? "bg-[#0AD69F] text-white" : "bg-[#EF476F] text-white"} ${role === "ROLE_STOREKEEPER" ? "cursor-pointer" : ""}`}
        onClick={role === "ROLE_STOREKEEPER" && order.deliveryNote ? () => handleStatutClick("BL") : null}

      >
        BL
      </span>
    </div>
  );

  const handleStatutClick = (key) => {
    switch (key) {
      case "BL":
        handleConsultBL();
        break;
      default:
        break;
    }
  };

  const handleConsultBL = () => {
    console.log("order.deliveryNote", order.deliveryNote);
    if (order.deliveryNote?.id) {
      console.log("order.deliveryNote.id", order.deliveryNote.id);
      history.push({
        pathname: `/delivery-notes/details/${order.deliveryNote.id}`,
      });
    }
  };

  const renderActionButtons = () => {
    // Only render buttons if user is ADMIN or STOREKEEPER
    if (role !== "ROLE_ADMIN" && role !== "ROLE_STOREKEEPER") {
      return null;
    }

    return (
      <>
        {/* Validate button - show when status is CREATED */}
        {order.status === "CREATED" && (
          <button
            className="flex items-center justify-center bg-[#0AD69F] text-[#FFFFFF] font-bold text-[14px] px-4 py-2 rounded-[12px] gap-x-2 shadow-[0px_20px_80px_#00000011] disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => handleUpdateStatus("VALIDATED")}
            disabled={updatingStatus}
          >
            {updatingStatus ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>{'Chargement...'}</span>
              </>
            ) : (
              <>
                <img src={checkIcon} alt="" className="w-5 h-5" />
                {t(`actions.validate`)}
              </>
            )}
          </button>
        )}

        {/* Mark as delivered button - show when status is VALIDATED */}
        {order.status === "VALIDATED" && (
          <button
            className="flex items-center justify-center bg-[#0AD69F] text-[#FFFFFF] font-bold text-[14px] px-4 py-2 rounded-[12px] shadow-[0px_20px_80px_#00000011] disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => handleUpdateStatus("DELIVERED")}
            disabled={updatingStatus}
          >
            {updatingStatus ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                <span>{'Chargement...'}</span>
              </>
            ) : (
              t(`actions.mark_as_delivered`)
            )}
          </button>
        )}
      </>
    );
  };

  if (loading) return <LoadingLogo />;


  return (
    <div>
      <Helmet>
        <title>Drivnest - {t("titles.order_details")}</title>
      </Helmet>
      {/* Header Section */}
      <div className="mb-4">
        <Breadcrumbs />
        <h1 className="text-[40px] font-semibold text-[#000000] ">{t(`titles.order_details`)}</h1>
        {/* State and Actions */}
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-[10px]">
            <p className="text-[#000000] font-semibold text-[24px]">
              {order.cartNumber}
            </p>
              <div className="flex space-x-1">{renderStatut()}</div>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-4">{renderActionButtons(order.status)}</div>

        </div>

        {/* Client Info */}
        <p className="text-primary text-[20px] font-semibold">
          {order.client?.code} - {order.client?.name}
        </p>
      </div>

      {/* Order Information */}
      <div className="w-full flex justify-evenly bg-white py-4 px-4 rounded-[12px] shadow-[0px_6px_26px_#626B7D12] mb-4">
        {/* DATE DE CRÉATION */}
        <div className="flex-1 text-center">
          <p className="text-[#707070] text-[14px] font-semibold mb-1">{t(`rows.creationDate`)}</p>
          <p className="text-[#38485C] text-[20px] font-semibold">
            {formatDateTime(order.createdAt)}
          </p>
        </div>

        {/* Fiche */}
        <div className="flex-1 text-center">
          <p className="text-[#707070] text-[14px] font-semibold mb-1">NUMÉRO DE PROJET</p>
          <p className="text-[#38485C] text-[20px] font-semibold">
            {order.projectNumber}
          </p>
        </div>

        {/* Véhicule */}
        <div className="flex-1 text-center">
          <p className="text-[#707070] text-[14px] font-semibold mb-1">CRÉE PAR</p>
          <p className="text-[#38485C] text-[20px] font-semibold">
            {order.user.firstName} {order.user.lastName}
          </p>
        </div>

        {/* TOTAL HT */}
        <div className="flex-1 text-center">
          <p className="text-[#707070] text-[14px] font-semibold mb-1">TOTAL HT</p>
          <p className="text-[#38485C] text-[20px] font-semibold">{formatAmount(order.priceHT)}</p>
        </div>

        {/* TOTAL TTC */}
        <div className="flex-1 text-center">
          <p className="text-[#707070] text-[14px] font-semibold mb-1">TOTAL TTC</p>
          <p className="text-[#38485C] text-[20px] font-semibold">{formatAmount(order.priceTTC)}</p>
        </div>
      </div>


      {/* Product List */}
      <h3 className="text-[16px] font-medium text-[#ACAFC6] mb-4">
        {t(`fields_types.list`)}
      </h3>
      <div className="space-y-4">
        {order.details.map((product, index) => (
          <ProductRow key={index} product={product} />
        ))}
      </div>


      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        manual={snackbar.manual}
      />

    </div>
  );
};

export default OrderDetails;

